import { Badge, Button, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useSetMatterPhaseAssignment } from "@/lib/data/react-query/matters/matter-phase";
import { ACTION_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type { MatterPhase, MatterPhaseAssignmentInput } from "@/lib/types";
import { PhaseTeamRoster } from "./PhaseTeamRoster";
import { PhaseAssignmentSelect } from "./PhaseAssignmentSelect";

export function MatterPhaseTeamRow({
  matterId,
  generationId,
  phase,
}: {
  matterId: string;
  generationId: string;
  phase: MatterPhase;
}) {
  const { hasGrant } = useUser();
  const mutation = useSetMatterPhaseAssignment();
  const team = phase.assignment ?? phase.team;
  const assignment = phase.assignment;
  const currentUser = Boolean(assignment?.selectedUserId);
  const selected = Boolean(team?.selectedTeamId || assignment?.selectedUserId);
  const defaultUser = Boolean(assignment?.configuredUserId);
  const currentKey =
    currentUser || (!selected && defaultUser)
      ? ACTION_PERMISSION_KEYS.matterAssignee
      : ACTION_PERMISSION_KEYS.matterTeamConnection;
  const canRemove = hasGrant(currentKey, "delete");
  const canTeam =
    hasGrant(
      ACTION_PERMISSION_KEYS.matterTeamConnection,
      team?.active && !currentUser ? "update" : "create",
    ) &&
    (!selected || !currentUser || canRemove);
  const canUser =
    hasGrant(
      ACTION_PERMISSION_KEYS.matterAssignee,
      team?.active && currentUser ? "update" : "create",
    ) &&
    (!selected || currentUser || canRemove);
  const canAssign = canTeam || canUser;
  const canRestore = defaultUser
    ? canUser
    : team?.configuredTeamId
      ? canTeam
      : selected
        ? canRemove
        : hasGrant(ACTION_PERMISSION_KEYS.matterTeamConnection, "create");
  const change = (
    mode: MatterPhaseAssignmentInput["mode"],
    target: {
      teamId?: string | null;
      userId?: string | null;
      roleId?: string | null;
    } = {},
  ) => {
    mutation.mutate({
      matterId,
      phaseId: phase.id,
      expectedGenerationId: generationId,
      expectedStateVersion: phase.stateVersion,
      mode,
      ...target,
    });
  };
  if (!selected && !canAssign && !canRemove) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
      <div className="min-w-0 flex-1 space-y-2">
        <Text variant="caption">Phase assignment</Text>
        <PhaseAssignmentSelect
          value={{
            teamId: team?.selectedTeamId,
            teamLabel: team?.selectedTeamLabel,
            userId: assignment?.selectedUserId,
            userLabel: assignment?.selectedUserLabel,
            roleId: assignment?.selectedRoleId,
            roleLabel: assignment?.selectedRoleLabel,
            userAvailable: assignment?.userAvailable,
          }}
          disabled={mutation.isPending}
          readOnly={!canAssign}
          allowTeam={canTeam}
          allowUser={canUser}
          allowClear={canRemove}
          onChange={(target) => {
            change(
              target.teamId || target.userId ? "Manual" : "Unassigned",
              target,
            );
          }}
        />
        {assignment?.selectedUserId && !assignment.userAvailable ? (
          <Text variant="caption">
            This individual is unavailable. Select another assignee.
          </Text>
        ) : null}
        {team?.selectedTeamId ? (
          <PhaseTeamRoster teamId={team.selectedTeamId} />
        ) : null}
        <div className="flex flex-wrap gap-2">
          {selected ? (
            <Badge variant="outline">
              {team?.active
                ? "Assigned"
                : team?.endedAt
                  ? "Assignment ended"
                  : phase.effectiveStatus === "Complete" ||
                      phase.effectiveStatus === "Closed"
                    ? "Phase finished · no active assignment"
                    : "Assigned when phase starts"}
            </Badge>
          ) : null}
          <Text variant="caption">
            {team?.mode === "Manual"
              ? "Manually selected"
              : team?.mode === "Unassigned"
                ? "Manually left unassigned"
                : "From project"}
          </Text>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {selected && canRemove ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => {
              change("Unassigned");
            }}
          >
            Remove assignment
          </Button>
        ) : null}
        {team?.mode !== "ProjectDefault" && canRestore ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={mutation.isPending}
            onClick={() => {
              change("ProjectDefault");
            }}
          >
            Use project default
          </Button>
        ) : null}
      </div>
    </div>
  );
}
